import Modal from '@/core/components/modal/Modal.tsx'
import { Box, Button, Stack } from '@mui/material'
import SelectComponentControlled from '@/core/components/form/selectComponents/SelectComponentControlled.tsx'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Supplier } from '@/modules/sales/clients/types/Supplier.ts'
import { useAppDispatch } from '@/globalStore/hooks.ts'
import useUninstallAppBySupplierLogic from '@/modules/utilities/uninstallApp/components/containers/useUninstallAppBySupplierLogic.ts'
import UninstallAppModal from '@/modules/utilities/uninstallApp/components/uninstallApp/UninstallAppModal.tsx'
import { useUninstallBySupplierContext } from '@/modules/utilities/uninstallApp/provider/UninstallAppBySupplierProvider.tsx'
import { uninstallAppActions } from '@/modules/utilities/uninstallApp/store/uninstallAppSlice/UninstallAppSlice.ts'

const UninstallAppBySupplierModalContainer = () => {
  const dispatchGlobal = useAppDispatch()
  const [step, setStep] = useState<'SELECT_BRAND_MODEL' | 'UNINSTALL_ARTICLE'>(
    'SELECT_BRAND_MODEL'
  )
  const [supplier, setSupplier] = useState<Supplier | null>(null)
  const [modalTitle, setModalTitle] = useState('Desinstalar artículo')
  const [nextBtnText, setNextBtnText] = useState('Continuar')
  const [backBtnText, setBackBtnText] = useState('Cancelar')
  const { setArticleData } = useUninstallBySupplierContext()

  const {
    control,
    trigger,
    watch,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(
      z.object({
        brandId: z.string({ message: 'Requerido' }).nonempty('Requerido')
      })
    )
  })

  const brandId = watch('brandId')

  const { brands, getSupplier, isLoadingSupplier } =
    useUninstallAppBySupplierLogic()

  const SelectBrandModelStep = useMemo(
    () => (
      <Stack spacing={2}>
        <SelectComponentControlled
          fullWidth
          label="Marca"
          helperText={errors.brandId?.message}
          error={!!errors.brandId?.message}
          options={brands.options}
          loading={brands.isLoading}
          name="brandId"
          control={control}
        />
      </Stack>
    ),
    [control, errors, brands]
  )

  const UI = useCallback(() => {
    switch (step) {
      case 'SELECT_BRAND_MODEL':
        return SelectBrandModelStep
      case 'UNINSTALL_ARTICLE': {
        if ([supplier?.code].some((v) => !v)) {
          throw new Error('There is NO provider')
        }
        return <UninstallAppModal />
      }
    }
  }, [step, SelectBrandModelStep, supplier])

  useEffect(() => {
    if (step === 'SELECT_BRAND_MODEL') {
      setNextBtnText('Desinstalar artículo')
      setBackBtnText('Cancelar')
      setModalTitle('Desinstalar artículo')
    } else if (step === 'UNINSTALL_ARTICLE') {
      setNextBtnText('')
      setBackBtnText('Atrás')
      setModalTitle(`Desinstalar artículo: ${supplier?.name}`)
    }
  }, [step, supplier?.name])

  const handleNextBtn = useCallback(async () => {
    if (step === 'SELECT_BRAND_MODEL') {
      let supplierData: Supplier | null = null
      if (await trigger(['brandId'], { shouldFocus: true })) {
        supplierData = await getSupplier(brandId)
        setSupplier(supplierData)
        setStep('UNINSTALL_ARTICLE')
      }
      if (!supplierData) throw new Error('No supplier data given')
      const articleData = {
        brandId,
        brandName: brands.options.find((b) => b.id === brandId)?.label || '',
        supplier: supplierData
      }
      setArticleData(articleData)
    }
  }, [brandId, brands.options, step])

  const handleBackBtn = useCallback(() => {
    if (step === 'SELECT_BRAND_MODEL') {
      dispatchGlobal(uninstallAppActions.closeModal())
    } else if (step === 'UNINSTALL_ARTICLE') {
      setStep('SELECT_BRAND_MODEL')
    }
  }, [step])

  return (
    <Modal
      open={true}
      title={modalTitle}
      maxWidth={600}
      fullWidth
      onClose={() => dispatchGlobal(uninstallAppActions.closeModal())}
    >
      <Box p={2}>
        {UI()}
        <Stack direction="row" gap={2} mt={2} justifyContent="end">
          <Button onClick={handleBackBtn}>{backBtnText}</Button>
          {nextBtnText && (
            <Button
              variant="contained"
              onClick={handleNextBtn}
              loading={isLoadingSupplier}
            >
              {nextBtnText}
            </Button>
          )}
        </Stack>
      </Box>
    </Modal>
  )
}

export default UninstallAppBySupplierModalContainer
